import type React from "react";
import { createContext, useContext } from "react";

import type { UserInfo } from "../../shared/user";
import { useGetSessionQuery, useSignOutMutation } from "@/store/slices/authSlice";

interface AuthContextType {
  isLoggedIn: boolean;
  user: UserInfo | null;
  loading: boolean;
  signOut: () => Promise<void>;
  getUserId: () => string | null;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  // Automatically fetches the session on mount and subscribes to changes
  const { data, isFetching: loading } = useGetSessionQuery();
  const [signOutMutation] = useSignOutMutation();

  const user = data?.user ?? null;
  const isLoggedIn = user !== null;

  const signOut = async () => {
    await signOutMutation().unwrap();
  };

  const getUserId = () => {
    return user?.id || null;
  };

  const value = {
    isLoggedIn,
    user,
    loading,
    signOut,
    getUserId,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
